import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import firebaseService from '../services/firebaseService';
import { formatCurrency, getQuarterAndYear, getFinancialYearString } from '../services/gstCalculations';
import { 
  ShoppingCartIcon, 
  CurrencyRupeeIcon, 
  DocumentTextIcon,
  ChartBarIcon,
  PlusIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';

function Dashboard() {
  const { userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalPurchases: 0,
    totalSales: 0,
    totalITC: 0,
    totalOutputGST: 0,
    netGST: 0,
    purchaseCount: 0,
    saleCount: 0
  });
  const [currentQuarter, setCurrentQuarter] = useState(null);
  const [recentTransactions, setRecentTransactions] = useState([]);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Get current quarter and year
      const now = new Date();
      const { quarter, year } = getQuarterAndYear(now);
      setCurrentQuarter({ quarter, year });

      // Load purchases and sales for current quarter
      const [purchases, sales] = await Promise.all([
        firebaseService.getPurchases({ quarter, year }),
        firebaseService.getSales({ quarter, year })
      ]);

      // Calculate stats
      const purchaseStats = purchases.reduce((acc, purchase) => ({
        totalAmount: acc.totalAmount + (purchase.totalAmount || 0),
        totalITC: acc.totalITC + (purchase.itcAmount || 0)
      }), { totalAmount: 0, totalITC: 0 });

      const salesStats = sales.reduce((acc, sale) => ({
        totalAmount: acc.totalAmount + (sale.totalAmount || 0),
        totalGST: acc.totalGST + (sale.gstAmount || 0)
      }), { totalAmount: 0, totalGST: 0 });

      const netGST = salesStats.totalGST - purchaseStats.totalITC;

      setStats({
        totalPurchases: purchaseStats.totalAmount,
        totalSales: salesStats.totalAmount,
        totalITC: purchaseStats.totalITC,
        totalOutputGST: salesStats.totalGST,
        netGST: netGST,
        purchaseCount: purchases.length,
        saleCount: sales.length
      });

      // Get recent transactions (last 5 of each)
      const recentPurchases = purchases.slice(0, 5).map(p => ({
        ...p,
        type: 'purchase',
        date: p.purchaseDate,
        amount: p.totalAmount,
        description: `Purchase from ${p.supplierName}`
      }));

      const recentSales = sales.slice(0, 5).map(s => ({
        ...s,
        type: 'sale',
        date: s.saleDate,
        amount: s.totalAmount,
        description: `Sale to ${s.customerName || 'Customer'}`
      }));

      const combined = [...recentPurchases, ...recentSales]
        .sort((a, b) => new Date(b.date) - new Date(a.date))
        .slice(0, 8);

      setRecentTransactions(combined);

    } catch (error) {
      console.error('Error loading dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon: Icon, trend, trendValue, color = 'primary' }) => (
    <div className="stat-card">
      <div className="flex items-center">
        <div className="flex-shrink-0">
          <Icon className={`h-8 w-8 text-${color}-600`} />
        </div>
        <div className="ml-5 w-0 flex-1">
          <dl>
            <dt className="text-sm font-medium text-gray-500 truncate">{title}</dt>
            <dd className="flex items-baseline">
              <div className="text-2xl font-semibold text-gray-900">
                {typeof value === 'number' ? formatCurrency(value) : value}
              </div>
              {trend && (
                <div className={`ml-2 flex items-baseline text-sm font-semibold ${
                  trend === 'up' ? 'text-green-600' : 'text-red-600'
                }`}>
                  {trend === 'up' ? (
                    <ArrowTrendingUpIcon className="self-center flex-shrink-0 h-4 w-4" />
                  ) : (
                    <ArrowTrendingDownIcon className="self-center flex-shrink-0 h-4 w-4" />
                  )}
                  <span className="sr-only">{trend === 'up' ? 'Increased' : 'Decreased'} by</span>
                  {trendValue}
                </div>
              )}
            </dd>
          </dl>
        </div>
      </div>
    </div>
  );

  const QuickAction = ({ title, description, href, icon: Icon, color = 'primary' }) => (
    <Link
      to={href}
      className={`relative group bg-white p-6 focus-within:ring-2 focus-within:ring-inset focus-within:ring-${color}-500 rounded-lg shadow hover:shadow-md transition-shadow`}
    >
      <div>
        <span className={`rounded-lg inline-flex p-3 bg-${color}-50 text-${color}-600 ring-4 ring-white`}>
          <Icon className="h-6 w-6" />
        </span>
      </div>
      <div className="mt-8">
        <h3 className="text-lg font-medium">
          <span className="absolute inset-0" aria-hidden="true" />
          {title}
        </h3>
        <p className="mt-2 text-sm text-gray-500">{description}</p>
      </div>
      <span className="pointer-events-none absolute top-6 right-6 text-gray-300 group-hover:text-gray-400">
        <PlusIcon className="h-6 w-6" />
      </span>
    </Link>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Welcome back, {userProfile?.displayName || 'User'}!
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            {currentQuarter && (
              <>
                Current Quarter: {currentQuarter.quarter} {getFinancialYearString(new Date(currentQuarter.year, 3, 1))}
              </>
            )}
          </p>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Sales"
          value={stats.totalSales}
          icon={CurrencyRupeeIcon}
          color="success"
        />
        <StatCard
          title="Total Purchases"
          value={stats.totalPurchases}
          icon={ShoppingCartIcon}
          color="primary"
        />
        <StatCard
          title="ITC Available"
          value={stats.totalITC}
          icon={DocumentTextIcon}
          color="warning"
        />
        <StatCard
          title="Net GST Payable"
          value={stats.netGST}
          icon={ChartBarIcon}
          color={stats.netGST >= 0 ? 'error' : 'success'}
        />
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ShoppingCartIcon className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Purchase Invoices</dt>
                  <dd className="text-lg font-medium text-gray-900">{stats.purchaseCount}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CurrencyRupeeIcon className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Sale Invoices</dt>
                  <dd className="text-lg font-medium text-gray-900">{stats.saleCount}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <DocumentTextIcon className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Output GST</dt>
                  <dd className="text-lg font-medium text-gray-900">{formatCurrency(stats.totalOutputGST)}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div>
        <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <QuickAction
            title="Add Purchase"
            description="Record a new purchase invoice"
            href="/purchases"
            icon={ShoppingCartIcon}
            color="primary"
          />
          <QuickAction
            title="Add Sale"
            description="Record a new sale transaction"
            href="/sales"
            icon={CurrencyRupeeIcon}
            color="success"
          />
          <QuickAction
            title="View Reports"
            description="Generate GST reports and summaries"
            href="/reports"
            icon={ChartBarIcon}
            color="warning"
          />
          <QuickAction
            title="Manage Suppliers"
            description="Add or edit supplier information"
            href="/suppliers"
            icon={DocumentTextIcon}
            color="primary"
          />
        </div>
      </div>

      {/* Recent Transactions */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Recent Transactions</h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">Latest purchases and sales</p>
        </div>
        <ul className="divide-y divide-gray-200">
          {recentTransactions.length > 0 ? (
            recentTransactions.map((transaction, index) => (
              <li key={index}>
                <div className="px-4 py-4 flex items-center justify-between">
                  <div className="flex items-center">
                    <div className={`flex-shrink-0 w-2.5 h-2.5 rounded-full ${
                      transaction.type === 'purchase' ? 'bg-primary-500' : 'bg-success-500'
                    }`}></div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-900">{transaction.description}</p>
                      <p className="text-sm text-gray-500">
                        {new Date(transaction.date).toLocaleDateString('en-IN')}
                      </p>
                    </div>
                  </div>
                  <div className="text-sm font-medium text-gray-900">
                    {formatCurrency(transaction.amount)}
                  </div>
                </div>
              </li>
            ))
          ) : (
            <li className="px-4 py-8 text-center text-gray-500">
              <p>No recent transactions</p>
              <p className="text-sm">Start by adding your first purchase or sale</p>
            </li>
          )}
        </ul>
        {recentTransactions.length > 0 && (
          <div className="bg-gray-50 px-4 py-3 text-center">
            <Link
              to="/reports"
              className="text-sm font-medium text-primary-600 hover:text-primary-500"
            >
              View all transactions →
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

export default Dashboard;
